import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    calendrierAPI, periodesAPI, evenementsAPI, exportAPI, brandingAPI
} from '../services/api';
import { couleurEvenement } from '../services/evenementsCategories';
import { peutModifier } from '../services/droits';
import {
    construireColonnes, construireCellStates, grouperParMois, sousNiveauDeColonne
} from '../services/planningPeriodes';
import { chargerImagePourPdf } from '../services/planningPdfCommun';
import { genererPdfCalendrier } from '../services/planningPdfCalendrier';
import PlanningTopbar from './PlanningTopbar';
import PanneauFlottant from './PanneauFlottant';
import ModalePlanning from './ModalePlanning';
import PlanningAnnuelEnvoi from './PlanningAnnuelEnvoi';
import './PlanningApp.css';

/**
 * ONGLET « EXPORT PDF CALENDRIER » (lot 5.6) — vue `#view-export` de la maquette.
 *
 * IL REMPLACE UNE MODALE, ET C'EST LE POINT DU LOT. `ExportPDF.js` empilait dans
 * une boîte de 750 px : la liste des colonnes, un dépliant par colonne, le titre,
 * les types, les 30 événements à cocher — puis produisait UN FICHIER PAR COLONNE
 * sans jamais rien montrer avant de télécharger. Régler un export revenait à
 * générer, ouvrir le PDF, revenir, corriger, regénérer.
 *
 * Ici : deux colonnes (réglages à gauche, aperçu à droite), les trois réglages
 * volumineux dans des PANNEAUX FLOTTANTS qu'on ouvre à la demande, et l'aperçu
 * qui est LE PDF LUI-MÊME — pas une imitation en HTML. Le dessin de la maquette
 * reconstitue la page en `div` parce qu'un fichier HTML seul ne sait pas faire
 * autrement ; ici jsPDF tourne déjà, et un aperçu qui n'est pas le document
 * finit toujours par mentir sur un détail (écart E21, écrit en spec).
 *
 * ── LES MODÈLES ENREGISTRÉS (lot 5.6 bis) ────────────────────────────────────
 * La carte de gauche s'appelle « Modèles d'export enregistrés » et non
 * « Documents générés (serveur) » comme la maquette : ON NE STOCKE AUCUN PDF.
 * Règle transverse du 2026-08-08, confirmée par Bertrand le 2026-08-12 — un
 * export du Planning est régénérable et sans valeur d'acte, donc la table
 * `planning_annuel_exports` (v37) porte LA CONFIGURATION, pas le fichier.
 *
 * Deux conséquences, dites à l'écran plutôt que découvertes :
 *   · « Télécharger » REFABRIQUE le PDF avec le planning d'aujourd'hui. Le
 *     fichier diffusé en septembre n'est pas retrouvable à l'identique.
 *   · « planning modifié depuis » ne signale donc pas un document périmé, mais un
 *     RÉSULTAT QUI VA DIFFÉRER de celui qu'on avait (empreinte du contenu,
 *     calculée côté serveur — voir services/planningEmpreinte.js).
 *
 * ⚠️ ÉCART E27 — LA MAQUETTE PORTE UN SEUL BOUTON « Générer & enregistrer », il y
 * en a deux ici. Sous la règle « configuration seule », générer et enregistrer
 * sont deux gestes distincts : sortir le PDF de la semaine ne doit pas créer un
 * modèle de plus dans la liste. « Générer le PDF » télécharge (et mémorise les
 * réglages courants) ; « Enregistrer le modèle » demande un nom et range.
 */

/* ═══ LE ZOOM D'AJUSTEMENT SE CALCULE, IL NE SE DEVINE PAS ═══════════════════
   « À 85 % on ne voit pas toute la page » (Bertrand, recette du lot 5.6). Exact :
   85 % est un pourcentage de la TAILLE RÉELLE, il ne dit rien du cadre où la page
   s'affiche. Une A4 paysage fait 297 × 210 mm, soit 1122 × 794 pixels CSS à 100 %
   (96 points par pouce) : le facteur qui la fait tenir se déduit donc de la taille
   du cadre, mesurée à l'écran.

   ⚠️ On ne peut PAS s'en remettre à `view=Fit` de la visionneuse : mesuré au banc
   d'essai le 2026-08-09, ce paramètre n'est pas honoré — la visionneuse rouvre le
   document au dernier zoom utilisé. Seul `zoom=<pourcentage>` fonctionne. */
const PAGE_PX_L = 297 / 25.4 * 96;
const PAGE_PX_H = 210 / 25.4 * 96;
const MARGE_VISIONNEUSE = 26;    // barres de défilement + liseré de la visionneuse

function zoomDAjustement(cadre) {
    if (!cadre) return 85;
    const l = cadre.clientWidth - MARGE_VISIONNEUSE;
    const h = cadre.clientHeight - MARGE_VISIONNEUSE;
    if (l <= 0 || h <= 0) return 85;
    return Math.max(20, Math.min(200, Math.floor(Math.min(l / PAGE_PX_L, h / PAGE_PX_H) * 100)));
}

/** « BUT1 » -> « BUT 1 » : `levelLabel` de la maquette. */
function libelleNiveau(code) {
    return String(code || '').replace(/([A-Za-z]+)(\d+)/, '$1 $2');
}

function PlanningExportCalendrier({ user, config, onglet, onChangeOnglet, anneeId, modeleDemande, onModeleDemandeTraite }) {
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [calendrier, setCalendrier] = useState([]);
    const [anneeScolaire, setAnneeScolaire] = useState(null);
    const [cellStates, setCellStates] = useState({});
    const [evenements, setEvenements] = useState([]);
    const [logo, setLogo] = useState(null);
    // ⚠️ LE LOGO DE L'UNIVERSITÉ SE LIT SUR `/api/branding`, PAS SUR
    // `/api/admin/settings`. Les deux le renvoient, mais la seconde exige d'être
    // super-administrateur : un chef de département aurait reçu 403 et son PDF
    // serait sorti sans logo, sans que rien ne l'explique. `/api/branding` est la
    // route ouverte — c'est déjà elle qui sert la page de connexion.
    const [logoUniversite, setLogoUniversite] = useState(null);

    // Réglages de l'export. Les ensembles sont des LISTES D'EXCEPTIONS : tout est
    // affiché par défaut, on ne stocke que ce qu'on éteint. Un événement créé
    // demain apparaît donc dans les exports sans que personne ait à le cocher.
    const [colonnesEteintes, setColonnesEteintes] = useState(() => new Set());
    const [typesEteints, setTypesEteints] = useState(() => new Set());
    const [evtEteints, setEvtEteints] = useState(() => new Set());
    const [libelles, setLibelles] = useState({});
    const [titre, setTitre] = useState('');
    const [titreModifie, setTitreModifie] = useState(false);

    const [panneaux, setPanneaux] = useState({ colonnes: false, types: false, evenements: false });
    const [recherche, setRecherche] = useState('');
    // `null` tant que le cadre n'a pas été mesuré : le zoom d'ouverture est
    // l'ajustement à la page, pas une valeur écrite d'avance.
    const [zoom, setZoom] = useState(null);
    const [ajustement, setAjustement] = useState(85);
    const cadreRef = useRef(null);
    const [apercu, setApercu] = useState(null);      // { url, pages, evenements }
    const [enregistrement, setEnregistrement] = useState(false);
    const [information, setInformation] = useState(null);

    // Les modèles enregistrés (lot 5.6 bis). `modeleActif` n'est qu'un repère
    // visuel : charger un modèle recopie ses réglages dans l'écran, il n'y a
    // ensuite plus de lien entre les deux — c'est « Remplacer » qui le rétablit.
    const [modeles, setModeles] = useState([]);
    const [modeleActif, setModeleActif] = useState(null);
    const [nouveauModele, setNouveauModele] = useState(null);   // { nom }
    // Lot PA.3 : le modèle dont la fenêtre « Envoyer » est ouverte
    const [envoi, setEnvoi] = useState(null);
    const [confirmation, setConfirmation] = useState(null);     // { titre, icone, message, libelle, danger, action }

    const anneeEffective = anneeId || config?.anneeScolaireId;

    /* ═══ CE QUE PEUT UN COMPTE EN CONSULTATION (lot 5.7) ════════════════════
       Enregistrer, remplacer et supprimer un modèle sont des ÉCRITURES : le
       serveur les refuse en 403 à qui n'a que « consulter » sur le Planning.
       Jusqu'ici l'écran laissait cliquer et affichait le refus dans un bandeau —
       un bouton qui ne peut pas marcher doit être grisé, et dire pourquoi (règle
       du lot 3 du Bloc 0). Générer le PDF, en revanche, reste permis : c'est une
       lecture. Seule la mémorisation des réglages est alors passée. */
    const peutEcrire = peutModifier(user, config?.departement?.id, 'planning');
    const motifLectureSeule = "Vous avez « consulter » sur le Planning : "
        + 'la génération du PDF reste possible, pas l\'enregistrement des réglages.';
    const configEffective = useMemo(
        () => ({ ...config, anneeScolaireId: anneeEffective }),
        [config, anneeEffective]
    );

    const colonnes = useMemo(() => construireColonnes(configEffective), [configEffective]);
    const colonnesRetenues = useMemo(
        () => colonnes.filter(c => !colonnesEteintes.has(c.cle)),
        [colonnes, colonnesEteintes]
    );
    const typesJournees = useMemo(() => configEffective?.typesJournees || [], [configEffective]);
    const mois = useMemo(() => grouperParMois(calendrier), [calendrier]);

    /* ═══ CHARGEMENT ═════════════════════════════════════════════════════════
       Cet écran charge SES données. Il aurait pu recevoir celles de l'onglet
       Calendrier, mais il s'ouvre aussi directement par son URL — et un export
       qui ne s'affiche que si l'on est passé par le calendrier d'abord serait
       un piège pour le lien envoyé à un collègue. */
    // Le chargeur d'images vit dans `planningPdfCommun.js` depuis le lot 5.7 :
    // l'export Événements en a besoin pour les mêmes logos, et pour l'image que
    // peut porter un événement.
    useEffect(() => {
        let vivant = true;
        const deptId = config?.departement?.id;
        if (!deptId || !anneeEffective) { setChargement(false); return undefined; }

        (async () => {
            setChargement(true);
            setErreur(null);
            try {
                const [repCal, repPer, repEvt, repCat, repPref, repCfg, repMod, repBranding] = await Promise.all([
                    calendrierAPI.getByAnnee(anneeEffective),
                    periodesAPI.getAll(anneeEffective, { departementId: deptId }),
                    evenementsAPI.getConfig(deptId, { anneeScolaireId: anneeEffective }),
                    evenementsAPI.getCategories(deptId),
                    exportAPI.getPreferences(deptId, anneeEffective),
                    exportAPI.getConfig(deptId, anneeEffective),
                    exportAPI.getModeles(deptId, anneeEffective, 'calendrier'),
                    // Une identité visuelle absente ne doit pas emporter l'écran :
                    // le PDF sortira sans logo, pas en erreur.
                    brandingAPI.get().catch(() => ({ data: {} }))
                ]);
                if (!vivant) return;

                setModeles(repMod.data.modeles || []);

                const categories = repCat.data.categories || [];
                setCalendrier(repCal.data.calendrier || []);
                setAnneeScolaire(repCal.data.anneeScolaire || null);
                setCellStates(construireCellStates(repPer.data.periodes, configEffective));

                // Les événements arrivent RÉSOLUS : libellé, date, couleur du
                // référentiel. Le générateur de PDF ne saura rien des catégories
                // — c'est ce qui referme l'écart E17.
                const evts = (repEvt.data.evenements || [])
                    .filter(e => e.date_debut)
                    .map(e => ({
                        id: e.id,
                        date: String(e.date_debut).split('T')[0],
                        libelle: e.libelle,
                        couleur: couleurEvenement(e, categories)
                    }))
                    .sort((a, b) => (a.date < b.date ? -1 : 1));
                setEvenements(evts);

                amorcerReglages(repPref.data.preferences || {}, repCfg.data.config || {}, evts);

                // Les deux logos sont chargés de front : ils sont indépendants, et
                // l'un peut manquer sans priver l'autre.
                const [univ, dept] = await Promise.all([
                    chargerImagePourPdf(repBranding.data?.logo_universite),
                    chargerImagePourPdf(config.departement?.logo_url)
                ]);
                if (vivant) { setLogoUniversite(univ); setLogo(dept); }
            } catch (e) {
                if (vivant) setErreur("Le calendrier n'a pas pu être chargé : " + (e.response?.data?.error || e.message));
            } finally {
                if (vivant) setChargement(false);
            }
        })();

        return () => { vivant = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config?.departement?.id, anneeEffective]);

    /**
     * AMORÇAGE DES DEUX MATRICES — rien n'est à recocher (arbitrage B).
     *
     * Deux sources, dans cet ordre :
     *   1. `export_preferences`, rangé par SOUS-NIVEAU : la sélection d'événements
     *      que l'ancienne modale enregistrait depuis des années. C'est elle qui
     *      évite de repartir de 32 événements à re-décocher.
     *   2. `export_config`, rangé par colonne : les réglages du nouvel écran, et
     *      à défaut ceux de l'ancienne modale, dont les clés (« BUT1-FI ») sont
     *      les mêmes.
     */
    const amorcerReglages = (preferences, sauvegarde, evts) => {
        const cols = construireColonnes(configEffective);
        const typesOff = new Set();
        const evtOff = new Set();

        cols.forEach(col => {
            const sn = sousNiveauDeColonne(col, configEffective);
            const retenus = sn ? preferences[sn.id] : null;
            if (Array.isArray(retenus)) {
                evts.forEach(e => { if (!retenus.includes(e.id)) evtOff.add(col.cle + '|' + e.id); });
            }
            const anciensTypes = sauvegarde.types?.[col.cle];
            if (anciensTypes) {
                Object.keys(anciensTypes).forEach(code => {
                    if (anciensTypes[code] === false) typesOff.add(col.cle + '|' + code);
                });
            }
            const anciennesSel = sauvegarde.eventSelections?.[col.cle];
            if (anciennesSel) {
                Object.keys(anciennesSel).forEach(id => {
                    if (anciennesSel[id] === false) evtOff.add(col.cle + '|' + id);
                    else evtOff.delete(col.cle + '|' + id);
                });
            }
        });

        const propre = sauvegarde.calendrier || {};
        setColonnesEteintes(new Set(propre.colonnesEteintes || []));
        setTypesEteints(propre.typesEteints ? new Set(propre.typesEteints) : typesOff);
        setEvtEteints(propre.evtEteints ? new Set(propre.evtEteints) : evtOff);

        // Renommages : le nouvel écran renomme un événement GLOBALEMENT (maquette),
        // l'ancienne modale les rangeait par colonne. On ne reprend les anciens que
        // si toutes les colonnes portaient le MÊME libellé — deux libellés
        // différents pour un même événement n'ont pas de traduction ici, et on ne
        // devine pas lequel garder (écart E22).
        if (propre.libelles) {
            setLibelles(propre.libelles);
        } else if (sauvegarde.eventLabels) {
            const parEvt = {};
            const conflit = new Set();
            Object.values(sauvegarde.eventLabels).forEach(parCol => {
                Object.entries(parCol || {}).forEach(([id, val]) => {
                    if (parEvt[id] !== undefined && parEvt[id] !== val) conflit.add(id);
                    parEvt[id] = val;
                });
            });
            conflit.forEach(id => { delete parEvt[id]; });
            setLibelles(parEvt);
        }

        if (propre.titre) { setTitre(propre.titre); setTitreModifie(true); }
    };

    /* ═══ TITRE AUTOMATIQUE ══════════════════════════════════════════════════
       « BUT1 » et « BUT2 » cochés donnent « BUT 1 BUT 2 ». Il suit les colonnes
       tant que personne n'y a touché ; dès la première frappe il est à
       l'utilisateur, et « Titre auto » le lui rend. */
    const titreAuto = useMemo(() => {
        const vus = [];
        colonnesRetenues.forEach(c => {
            if (!vus.includes(c.niveauCode)) vus.push(c.niveauCode);
        });
        return vus.map(libelleNiveau).join(' ');
    }, [colonnesRetenues]);

    useEffect(() => {
        if (!titreModifie) setTitre(titreAuto);
    }, [titreAuto, titreModifie]);

    /* ═══ APERÇU — LE VRAI PDF ═══════════════════════════════════════════════
       Régénéré après une pause : cocher une colonne redessine 14 mois, et le
       faire à chaque case d'un glissé donnerait un écran qui saccade. */
    const urlPrecedente = useRef(null);
    useEffect(() => {
        if (chargement || !calendrier.length) return undefined;
        const minuteur = setTimeout(() => {
            try {
                const { pdf, pages, evenementsImprimes } = construirePdf();
                const url = URL.createObjectURL(pdf.output('blob'));
                if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
                urlPrecedente.current = url;
                setApercu({ url, pages, evenements: evenementsImprimes });
                setErreur(null);
            } catch (e) {
                setErreur("L'aperçu n'a pas pu être produit : " + e.message);
            }
        }, 350);
        return () => clearTimeout(minuteur);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargement, calendrier, cellStates, evenements, colonnesEteintes, typesEteints, evtEteints, libelles, titre, logo, logoUniversite]);

    useEffect(() => () => {
        if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
    }, []);

    // Le cadre est mesuré à l'affichage et à chaque redimensionnement de la
    // fenêtre : l'ajustement suit. Tant que l'utilisateur n'a pas touché au zoom,
    // l'aperçu reste ajusté ; dès qu'il l'a réglé, on ne lui reprend pas la main.
    useEffect(() => {
        if (chargement) return undefined;
        const mesurer = () => {
            const a = zoomDAjustement(cadreRef.current);
            setAjustement(a);
            setZoom(z => (z === null ? a : z));
        };
        mesurer();
        window.addEventListener('resize', mesurer);
        return () => window.removeEventListener('resize', mesurer);
    }, [chargement]);

    /* ═══ LES RÉGLAGES, DÉTACHÉS DE L'ÉCRAN ══════════════════════════════════
       `construirePdf()` sans argument dessine l'aperçu — les réglages courants.
       Avec un argument, il dessine CEUX D'UN MODÈLE, sans passer par l'état de
       React : « Télécharger » depuis la liste ne doit ni modifier l'écran, ni
       attendre un rendu. Poser les réglages dans l'état puis générer aussitôt
       aurait produit le PDF de l'état PRÉCÉDENT (un `setState` n'est pas lu dans
       la même passe) — un décalage d'un cran, invisible en relecture de code et
       parfaitement visible dans le fichier. */
    const reglagesCourants = () => ({ colonnesEteintes, typesEteints, evtEteints, libelles, titre });

    const reglagesDepuisConfig = (cfg) => ({
        colonnesEteintes: new Set(cfg?.colonnesEteintes || []),
        typesEteints: new Set(cfg?.typesEteints || []),
        evtEteints: new Set(cfg?.evtEteints || []),
        libelles: cfg?.libelles || {},
        titre: cfg?.titre || ''
    });

    /** La forme envoyée au serveur — la même que celle relue à l'ouverture. */
    const configCourante = () => ({
        colonnesEteintes: [...colonnesEteintes],
        typesEteints: [...typesEteints],
        evtEteints: [...evtEteints],
        libelles,
        // Le titre EFFECTIF, qu'il ait été tapé ou calculé : un modèle doit
        // reproduire le même document, pas recalculer un titre depuis d'autres
        // colonnes que celles qu'il porte.
        titre
    });

    const construirePdf = (reglages) => {
        const r = reglages || reglagesCourants();
        return genererPdfCalendrier({
            colonnes: colonnes.filter(c => !r.colonnesEteintes.has(c.cle)),
            mois,
            cellStates,
            typesJournees,
            typesEteints: r.typesEteints,
            evenements: evenements.map(e => ({ ...e, libelle: r.libelles[e.id] || e.libelle })),
            evtEteints: r.evtEteints,
            titre: r.titre,
            sousTitre: (config?.departement?.titre_planning || config?.departement?.nom || '')
                + (anneeScolaire?.libelle ? ' - Année universitaire ' + anneeScolaire.libelle : ''),
            auteur: [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.email || '',
            logo,
            logoUniversite
        });
    };

    /* ═══ BASCULES ═══════════════════════════════════════════════════════════ */
    const basculer = (setter, cle) => setter(prev => {
        const suivant = new Set(prev);
        if (suivant.has(cle)) suivant.delete(cle); else suivant.add(cle);
        return suivant;
    });

    const toutesColonnes = (allumees) => setColonnesEteintes(
        allumees ? new Set() : new Set(colonnes.map(c => c.cle))
    );

    const toutEvenements = (allumer) => setEvtEteints(prev => {
        const suivant = new Set(prev);
        colonnesRetenues.forEach(c => evenements.forEach(e => {
            if (allumer) suivant.delete(c.cle + '|' + e.id);
            else suivant.add(c.cle + '|' + e.id);
        }));
        return suivant;
    });

    const basculerColonneEntiere = (setter, cleColonne, elements) => setter(prev => {
        const suivant = new Set(prev);
        const toutAllume = elements.every(id => !suivant.has(cleColonne + '|' + id));
        elements.forEach(id => {
            if (toutAllume) suivant.add(cleColonne + '|' + id);
            else suivant.delete(cleColonne + '|' + id);
        });
        return suivant;
    });

    /* ═══ CLIC-GLISSÉ SUR LES MATRICES (façon tableur) ═══════════════════════
       Une matrice de 32 événements × 6 colonnes fait 192 cases : les cocher une
       par une n'est pas un usage, c'est une punition. L'état du glissement vit
       dans une réf — le relire à chaque survol de cellule provoquerait un rendu
       par cellule traversée. */
    const glisse = useRef(null);
    useEffect(() => {
        const fin = () => { glisse.current = null; document.body.style.userSelect = ''; };
        window.addEventListener('mouseup', fin);
        return () => window.removeEventListener('mouseup', fin);
    }, []);

    const debuterPeinture = (setter, cle, e) => {
        e.preventDefault();
        const eteindre = !cleEteinte(setter, cle);
        glisse.current = { setter, eteindre };
        document.body.style.userSelect = 'none';
        appliquer(setter, cle, eteindre);
    };
    const survolPeinture = (setter, cle) => {
        if (!glisse.current || glisse.current.setter !== setter) return;
        appliquer(setter, cle, glisse.current.eteindre);
    };
    const cleEteinte = (setter, cle) => (setter === setTypesEteints ? typesEteints : evtEteints).has(cle);
    const appliquer = (setter, cle, eteindre) => setter(prev => {
        if (prev.has(cle) === eteindre) return prev;
        const suivant = new Set(prev);
        if (eteindre) suivant.add(cle); else suivant.delete(cle);
        return suivant;
    });

    /* ═══ COMPTEURS DES BOUTONS ══════════════════════════════════════════════ */
    const nbTypesVisibles = typesJournees.filter(
        t => colonnesRetenues.some(c => !typesEteints.has(c.cle + '|' + t.code))
    ).length;
    const nbEvtVisibles = evenements.filter(
        e => colonnesRetenues.some(c => !evtEteints.has(c.cle + '|' + e.id))
    ).length;

    /* ═══ GÉNÉRATION ═════════════════════════════════════════════════════════ */
    const nomFichier = (pourTitre) => {
        const propre = (pourTitre || titre || 'Calendrier').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');
        return 'Calendrier_' + (config?.departement?.code || 'IUT') + '_' + propre + '.pdf';
    };

    const enregistrerReglages = async () => {
        const deptId = config?.departement?.id;
        if (!deptId || !anneeEffective) return;
        const existante = (await exportAPI.getConfig(deptId, anneeEffective)).data.config || {};
        await exportAPI.saveConfig({
            departementId: deptId,
            anneeScolaireId: anneeEffective,
            config: {
                ...existante,
                calendrier: {
                    colonnesEteintes: [...colonnesEteintes],
                    typesEteints: [...typesEteints],
                    evtEteints: [...evtEteints],
                    libelles,
                    titre: titreModifie ? titre : null
                }
            }
        });
        // Les préférences par sous-niveau restent tenues à jour : d'autres écrans
        // les lisent, et les abandonner ferait diverger deux vérités.
        for (const col of colonnes) {
            const sn = sousNiveauDeColonne(col, configEffective);
            if (!sn) continue;
            const retenus = evenements.filter(e => !evtEteints.has(col.cle + '|' + e.id)).map(e => e.id);
            await exportAPI.savePreferences({
                departementId: deptId,
                anneeScolaireId: anneeEffective,
                sousNiveauId: sn.id,
                evenementIds: retenus
            });
        }
    };

    const generer = async () => {
        if (!colonnesRetenues.length) {
            setInformation({ titre: 'Aucune colonne', message: 'Cochez au moins une colonne à inclure dans le PDF.' });
            return;
        }
        setEnregistrement(true);
        try {
            const { pdf } = construirePdf();
            pdf.save(nomFichier());
            if (peutEcrire) await enregistrerReglages();
            setInformation({
                titre: 'PDF généré',
                message: 'Le fichier « ' + nomFichier() + ' » a été téléchargé'
                    + (peutEcrire
                        ? ', et vos réglages sont mémorisés pour la prochaine ouverture de cet écran.'
                        : ". Vos réglages ne sont pas mémorisés : vous n'avez que « consulter » sur le Planning.")
            });
        } catch (e) {
            // Un enregistrement refusé remonte à l'écran : il ne finit pas dans la
            // console, où personne ne le lira (règle du lot 5.1).
            setErreur('Le PDF a été produit mais les réglages n\'ont pas pu être enregistrés : '
                + (e.response?.data?.error || e.message));
        } finally {
            setEnregistrement(false);
        }
    };

    /* ═══ LES MODÈLES — QUATRE GESTES ════════════════════════════════════════
       Voir · Télécharger · Remplacer par les réglages actuels · Supprimer.
       « Remplacer » a pris la place du « Régénérer » de la maquette : sans fichier
       stocké, régénérer ne voulait plus rien dire (Télécharger le fait déjà). Le
       geste qui manquait, lui, est bien réel — « j'ai ajusté les colonnes, je mets
       mon modèle à jour ». Décision Bertrand du 2026-08-12. */

    const echecModele = (e, quoi) => setErreur(
        quoi + " : " + (e.response?.data?.error || e.message)
    );

    const appliquerModele = (m) => {
        const r = reglagesDepuisConfig(m.config);
        setColonnesEteintes(r.colonnesEteintes);
        setTypesEteints(r.typesEteints);
        setEvtEteints(r.evtEteints);
        setLibelles(r.libelles);
        setTitre(r.titre);
        // Le titre du modèle est celui qu'on veut, pas celui que les colonnes
        // suggèrent : on le fige, « Titre auto » reste là pour le rendre au calcul.
        setTitreModifie(true);
        setModeleActif(m.id);
    };

    const telechargerModele = (m) => {
        const r = reglagesDepuisConfig(m.config);
        if (colonnes.every(c => r.colonnesEteintes.has(c.cle))) {
            setInformation({
                titre: 'Modèle sans colonne',
                message: 'Ce modèle ne retient aucune des colonnes actuelles du planning : '
                    + 'il n\'y a rien à imprimer. Charge-le pour le corriger, puis remplace-le.'
            });
            return;
        }
        try {
            const { pdf } = construirePdf(r);
            pdf.save(nomFichier(r.titre));
        } catch (e) {
            setErreur('Le PDF de ce modèle n\'a pas pu être produit : ' + e.message);
        }
    };

    const enregistrerModele = async (nom) => {
        const deptId = config?.departement?.id;
        if (!deptId || !anneeEffective) return;
        setEnregistrement(true);
        try {
            const rep = await exportAPI.creerModele({
                departementId: deptId,
                anneeScolaireId: anneeEffective,
                type: 'calendrier',
                titre: nom,
                config: configCourante(),
                // v94 (lot PA.2) : la case « sur l'accueil » de la fenêtre
                surAccueil: nouveauModele?.surAccueil === true
            });
            const cree = rep.data.modele;
            setModeles(liste => [cree, ...liste]);
            setModeleActif(cree.id);
            setNouveauModele(null);
            setInformation({
                titre: 'Modèle enregistré',
                message: 'Le modèle « ' + cree.titre + ' » est rangé et partagé avec le département. '
                    + 'Il garde vos réglages : le PDF, lui, sera refabriqué à chaque téléchargement.'
            });
        } catch (e) {
            // Un nom déjà pris n'est pas une faute, c'est un « Enregistrer » de trop
            // sur un modèle qu'on croyait mettre à jour : on propose le geste voulu.
            if (e.response?.status === 409 && e.response.data?.id) {
                const existant = modeles.find(m => m.id === e.response.data.id);
                setNouveauModele(null);
                setConfirmation({
                    titre: 'Ce nom est déjà pris',
                    icone: 'fa-triangle-exclamation',
                    message: 'Un modèle nommé « ' + nom + ' » existe déjà pour ce planning. '
                        + 'Voulez-vous le remplacer par les réglages actuels ?',
                    libelle: 'Remplacer',
                    action: () => (existant
                        ? remplacerModele(existant)
                        : Promise.resolve(setErreur('Le modèle « ' + nom + ' » existe déjà.')))
                });
            } else {
                echecModele(e, "Le modèle n'a pas pu être enregistré");
            }
        } finally {
            setEnregistrement(false);
        }
    };

    const remplacerModele = async (m) => {
        setEnregistrement(true);
        try {
            const rep = await exportAPI.remplacerModele(m.id, { config: configCourante() });
            const maj = rep.data.modele;
            setModeles(liste => liste.map(x => (x.id === maj.id ? maj : x)));
            setModeleActif(maj.id);
            setConfirmation(null);
            setInformation({
                titre: 'Modèle mis à jour',
                message: 'Le modèle « ' + maj.titre + ' » porte désormais les réglages affichés à l\'écran.'
            });
        } catch (e) {
            setConfirmation(null);
            echecModele(e, "Le modèle n'a pas pu être mis à jour");
        } finally {
            setEnregistrement(false);
        }
    };

    /* ═══ LOT PA.2 — « SUR L'ACCUEIL » ═══════════════════════════════════════
       La punaise d'une carte fait du modèle une VIGNETTE de l'accueil du module
       (v94) ; elle ne touche ni aux réglages ni à l'empreinte. */
    const basculerAccueilModele = async (m) => {
        try {
            await exportAPI.basculerAccueil(m.id, config?.departement?.id, !m.surAccueil);
            setModeles(liste => liste.map(x => (x.id === m.id ? { ...x, surAccueil: !m.surAccueil } : x)));
        } catch (e) {
            echecModele(e, "Le modèle n'a pas pu changer de place");
        }
    };

    // Une vignette de l'accueil demande CE modèle (et peut-être son PDF) : on le
    // charge une fois l'écran prêt — le PDF a besoin du calendrier, des périodes
    // et des logos, tous chargés avant que `chargement` ne retombe.
    useEffect(() => {
        if (!modeleDemande || chargement) return;
        const m = modeles.find(x => x.id === modeleDemande.id);
        if (!m) {
            setErreur("Ce modèle n'existe plus : il a peut-être été supprimé depuis l'accueil d'un collègue.");
        } else {
            appliquerModele(m);
            if (modeleDemande.telecharger) telechargerModele(m);
            if (modeleDemande.envoyer) setEnvoi(m);
        }
        onModeleDemandeTraite?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeleDemande, chargement, modeles]);

    const supprimerModele = async (m) => {
        setEnregistrement(true);
        try {
            await exportAPI.supprimerModele(m.id);
            setModeles(liste => liste.filter(x => x.id !== m.id));
            setModeleActif(actif => (actif === m.id ? null : actif));
            setConfirmation(null);
        } catch (e) {
            setConfirmation(null);
            echecModele(e, "Le modèle n'a pas pu être supprimé");
        } finally {
            setEnregistrement(false);
        }
    };

    const demanderRemplacement = (m) => setConfirmation({
        titre: 'Remplacer ce modèle ?',
        icone: 'fa-rotate',
        message: 'Le modèle « ' + m.titre + ' » prendra les réglages affichés à l\'écran '
            + '(colonnes, types, événements, titre). Ses réglages actuels seront perdus.',
        libelle: 'Remplacer',
        action: () => remplacerModele(m)
    });

    const demanderSuppression = (m) => setConfirmation({
        titre: 'Supprimer ce modèle ?',
        icone: 'fa-trash',
        message: 'Le modèle « ' + m.titre + ' » sera retiré de la liste, pour tout le département. '
            + 'Aucun document n\'est supprimé — il n\'y en a jamais eu : un modèle ne garde que des réglages.',
        libelle: 'Supprimer',
        danger: true,
        action: () => supprimerModele(m)
    });

    /** « 12/08/2026 14:05 » — jamais une date brute de la base à l'écran. */
    const dateCourte = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime())
            ? ''
            : d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    /* ═══ RENDU ══════════════════════════════════════════════════════════════ */
    const ouvrir = (cle, ouvert) => setPanneaux(p => ({ ...p, [cle]: ouvert }));

    // Le zoom réellement appliqué : celui de l'utilisateur, sinon l'ajustement
    // calculé sur le cadre. Jamais `null` — un `#zoom=null` ne veut rien dire.
    const zoomEffectif = zoom || ajustement;

    const evtFiltres = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        if (!q) return evenements;
        return evenements.filter(e => (libelles[e.id] || e.libelle).toLowerCase().includes(q));
    }, [evenements, libelles, recherche]);

    return (
        <div className="pl-root">
            <div className="sticky-top" style={{ margin: 0, top: 0 }}>
                <PlanningTopbar
                    sousTitre={(config?.departement?.nom || '')
                        + (anneeScolaire?.libelle ? ` · ${anneeScolaire.libelle}` : '')}
                    onglet={onglet}
                    onChangeOnglet={onChangeOnglet}
                />
            </div>

            <div className="pl-content">
                {/* La barre d'outils du dessin : le titre de la vue à gauche, l'action
                    à droite. Elle double le nom de l'onglet, et c'est voulu — la
                    maquette la porte, et c'est elle qui reste visible quand on fait
                    défiler la colonne des réglages. */}
                <div className="pl-toolbar pl-exp-barre">
                    <h2>Export PDF Calendrier</h2>
                    <span className="pl-grow" />
                    {/* Deux boutons là où la maquette en portait un (écart E27, en
                        tête de fichier) : générer un PDF et ranger un modèle ne sont
                        pas le même geste dès lors qu'on ne stocke que des réglages. */}
                    <button
                        className="pl-btn"
                        onClick={() => setNouveauModele({ nom: titre || 'Calendrier' })}
                        disabled={chargement || enregistrement || !colonnesRetenues.length || !peutEcrire}
                        title={peutEcrire
                            ? 'Garder ces réglages sous un nom, pour tout le département'
                            : motifLectureSeule}
                    >
                        <i className="fa-solid fa-floppy-disk" /> Enregistrer le modèle
                    </button>
                    <button
                        className="pl-btn pl-btn-primary"
                        onClick={generer}
                        disabled={chargement || enregistrement || !colonnesRetenues.length}
                    >
                        <i className="fa-solid fa-file-pdf" /> {enregistrement ? 'Génération…' : 'Générer le PDF'}
                    </button>
                </div>

                {erreur && (
                    <div className="pl-bandeau pl-bandeau-warn">
                        <i className="fa-solid fa-triangle-exclamation" /> {erreur}
                    </div>
                )}

                {chargement ? (
                    <div className="pl-exp-vide"><i className="fa-solid fa-spinner fa-spin" /> Chargement du calendrier…</div>
                ) : (
                <div className="pl-exp-2col">
                    {/* ══ RÉGLAGES ══════════════════════════════════════════ */}
                    <div className="pl-exp-left">
                        <div className="pl-card">
                            <div className="pl-card-header">
                                <span className="ttl"><i className="fa-solid fa-sliders" /> Configuration de l'export</span>
                            </div>
                            <div className="pl-card-body">
                                <div className="pl-exp-boutons">
                                    <button className="pl-btn pl-exp-openbtn" onClick={() => ouvrir('colonnes', true)}>
                                        <span><i className="fa-solid fa-table-columns" /> Colonnes à inclure</span>
                                        <span className="pl-badge pl-badge-info">{colonnesRetenues.length}/{colonnes.length}</span>
                                    </button>
                                    <button className="pl-btn pl-exp-openbtn" onClick={() => ouvrir('types', true)}>
                                        <span><i className="fa-solid fa-palette" /> Types de journées</span>
                                        <span className="pl-badge pl-badge-info">{nbTypesVisibles}/{typesJournees.length}</span>
                                    </button>
                                    <button className="pl-btn pl-exp-openbtn" onClick={() => ouvrir('evenements', true)}>
                                        <span><i className="fa-solid fa-thumbtack" /> Événements importants</span>
                                        <span className="pl-badge pl-badge-info">{nbEvtVisibles}/{evenements.length}</span>
                                    </button>
                                </div>

                                <div className="pl-exp-sec">Titre du PDF — auto depuis les colonnes</div>
                                <input
                                    className="pl-f pl-exp-titre"
                                    value={titre}
                                    onChange={e => { setTitreModifie(true); setTitre(e.target.value); }}
                                    placeholder={titreAuto}
                                />
                                {titreModifie && (
                                    <button className="pl-ver-restore" onClick={() => { setTitreModifie(false); setTitre(titreAuto); }}>
                                        <i className="fa-solid fa-rotate" /> Titre auto
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ══ MODÈLES ENREGISTRÉS ═══════════════════════════
                            La carte « Documents générés (serveur) » de la maquette,
                            sous son vrai nom : ce qui est rangé ici est un jeu de
                            RÉGLAGES. L'appeler « document » promettrait le fichier de
                            septembre, qu'on n'a pas — et qu'on n'aura jamais, par
                            décision et non par oubli. */}
                        <div className="pl-card">
                            <div className="pl-card-body pl-exp-docs">
                                <div className="pl-exp-sec">
                                    <i className="fa-solid fa-server" /> Modèles d'export enregistrés
                                </div>

                                {modeles.map(m => (
                                    <div
                                        key={m.id}
                                        className={'pl-exp-doc' + (modeleActif === m.id ? ' actif' : '')}
                                    >
                                        <i className="fa-solid fa-sliders" />
                                        <div className="pl-ed-meta">
                                            <b>{m.titre}</b>
                                            <span className="pl-hint">
                                                {dateCourte(m.updated_at || m.created_at)}
                                                {m.auteur ? ' · ' + m.auteur : ''}
                                            </span>
                                            {m.obsolete && (
                                                <span className="pl-ed-stale" title="Le PDF que vous obtiendrez ne sera pas celui d'alors">
                                                    <i className="fa-solid fa-triangle-exclamation" /> planning modifié depuis
                                                </span>
                                            )}
                                        </div>
                                        <button className="pl-ed-act" title="Charger ces réglages dans l'écran" onClick={() => appliquerModele(m)}>
                                            <i className="fa-solid fa-eye" />
                                        </button>
                                        <button className="pl-ed-act" title="Télécharger le PDF de ce modèle" onClick={() => telechargerModele(m)}>
                                            <i className="fa-solid fa-download" />
                                        </button>
                                        <button
                                            className={'pl-ed-act' + (m.surAccueil ? ' accueil-on' : '')}
                                            title={!peutEcrire ? motifLectureSeule
                                                : (m.surAccueil ? 'Vignette de l\'accueil — cliquer pour l\'en retirer' : 'Mettre ce modèle sur l\'accueil du module')}
                                            disabled={!peutEcrire}
                                            onClick={() => basculerAccueilModele(m)}
                                        >
                                            <i className="fa-solid fa-thumbtack" />
                                        </button>
                                        <button
                                            className="pl-ed-act"
                                            title={peutEcrire ? 'Envoyer le PDF de ce modèle par courriel (un message par personne)' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => { appliquerModele(m); setEnvoi(m); }}
                                        >
                                            <i className="fa-solid fa-paper-plane" />
                                        </button>
                                        <button
                                            className="pl-ed-act"
                                            title={peutEcrire ? 'Remplacer par les réglages actuels' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => demanderRemplacement(m)}
                                        >
                                            <i className="fa-solid fa-rotate" />
                                        </button>
                                        <button
                                            className="pl-ed-act danger"
                                            title={peutEcrire ? 'Supprimer ce modèle' : motifLectureSeule}
                                            disabled={!peutEcrire}
                                            onClick={() => demanderSuppression(m)}
                                        >
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                ))}

                                {!modeles.length && (
                                    <div className="pl-hint pl-exp-docs-vide">
                                        Aucun modèle enregistré. « Enregistrer le modèle » garde les réglages
                                        affichés sous un nom, pour tout le département.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* La règle est dite à l'écran, une fois, là où elle s'applique :
                            un utilisateur qui croit récupérer le fichier envoyé aux
                            étudiants ne doit pas l'apprendre en l'ouvrant. */}
                        <div className="pl-note">
                            <i className="fa-solid fa-circle-info" />
                            <span>
                                Un modèle garde <b>les réglages</b>, pas le fichier : télécharger
                                <b> refabrique</b> le PDF avec le planning d'aujourd'hui. Le document
                                obtenu est donc toujours à jour — mais ce n'est pas, à l'identique,
                                celui que vous aviez diffusé.
                            </span>
                        </div>
                    </div>

                    {/* ══ APERÇU ════════════════════════════════════════════ */}
                    <div className="pl-exp-right">
                        <div className="pl-card" style={{ marginBottom: 0 }}>
                            <div className="pl-card-header">
                                <span className="ttl"><i className="fa-solid fa-eye" /> Aperçu — le PDF tel qu'il sera</span>
                                <span className="pl-exp-zoom">
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(z => Math.max(20, (z || ajustement) - 15))} title="Dézoomer">
                                        <i className="fa-solid fa-magnifying-glass-minus" />
                                    </button>
                                    <span className="pl-exp-zlbl">
                                        {zoom || ajustement}%{zoom === ajustement && <> ajusté</>}
                                    </span>
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(z => Math.min(400, (z || ajustement) + 15))} title="Zoomer">
                                        <i className="fa-solid fa-magnifying-glass-plus" />
                                    </button>
                                    <button className="pl-btn pl-btn-sm" onClick={() => setZoom(ajustement)} title="Ajuster la page au cadre">
                                        <i className="fa-solid fa-arrows-to-dot" />
                                    </button>
                                </span>
                            </div>
                            <div className="pl-card-body">
                                <div className="pl-exp-preview-wrap" ref={cadreRef}>
                                    {apercu ? (
                                        /* ⚠️ LA CLÉ PORTE LE ZOOM, ET C'EST LA SEULE FAÇON
                                           DONT CE BOUTON PUISSE MARCHER. Changer le seul
                                           FRAGMENT de `src` (`#zoom=…`) est une navigation
                                           « dans le même document » : le navigateur ne
                                           recharge rien et la visionneuse ne relit jamais
                                           son paramètre. Les boutons +/− étaient donc
                                           inopérants — défaut relevé par Bertrand à la
                                           recette du lot 5.6. La clé force React à
                                           REMONTER l'iframe, donc à recharger le document,
                                           qui lit alors son zoom.

                                           Mesuré avant d'être corrigé (Edge piloté, deux
                                           captures d'écran comparées) : fragment seul =
                                           images identiques ; remontage = images
                                           différentes. Une mise à l'échelle CSS marchait
                                           aussi, mais elle agrandit une image déjà tramée
                                           — donc floue — au lieu de redessiner la page. */
                                        <iframe
                                            key={apercu.url + '@' + zoomEffectif}
                                            className="pl-exp-preview"
                                            title="Aperçu du PDF"
                                            src={apercu.url + '#toolbar=0&navpanes=0&zoom=' + zoomEffectif}
                                        />
                                    ) : (
                                        <div className="pl-exp-vide"><i className="fa-solid fa-spinner fa-spin" /> Composition du PDF…</div>
                                    )}
                                </div>
                                <div className="pl-exp-note">
                                    {apercu && (
                                        <>
                                            <b>{colonnesRetenues.length} colonne{colonnesRetenues.length > 1 ? 's' : ''}</b>
                                            {' '}· {mois.length} mois · {apercu.evenements} événement{apercu.evenements > 1 ? 's' : ''}
                                            {' '}· <b>{apercu.pages} page{apercu.pages > 1 ? 's' : ''}</b> A4 paysage
                                            {apercu.pages > 1 && ' (les événements qui ne tiennent pas passent en page 2)'}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* ══ PANNEAU 1 — COLONNES ═════════════════════════════════════ */}
            <PanneauFlottant
                ouvert={panneaux.colonnes}
                titre="Colonnes à inclure"
                icone="fa-table-columns"
                largeur={520}
                hauteur="46vh"
                onFermer={() => ouvrir('colonnes', false)}
                classeContenu="pl-floatp-b"
                sousBarre={
                    <div className="pl-exp-sousbarre">
                        <button className="pl-btn pl-btn-sm" onClick={() => toutesColonnes(true)}>Tout</button>
                        <button className="pl-btn pl-btn-sm" onClick={() => toutesColonnes(false)}>Aucun</button>
                        {/* La règle « une seule colonne, pas de nom » est annoncée là où
                            elle se déclenche. Une mise en page qui change toute seule
                            sans rien dire se lit comme un défaut. */}
                        <span className="pl-hint">
                            {colonnesRetenues.length === 1
                                ? "Une seule colonne : son nom n'apparaît pas dans le PDF — le titre le dit déjà, et le calendrier gagne la place"
                                : 'Le titre se construit depuis les niveaux cochés'}
                        </span>
                    </div>
                }
                pied={<button className="pl-btn pl-btn-primary" onClick={() => ouvrir('colonnes', false)}><i className="fa-solid fa-check" /> Terminé</button>}
            >
                <div className="pl-exp-cols">
                    {colonnes.map(c => (
                        <label key={c.cle} className="pl-fbtn">
                            <input
                                type="checkbox"
                                checked={!colonnesEteintes.has(c.cle)}
                                onChange={() => basculer(setColonnesEteintes, c.cle)}
                            />
                            <span className="pl-dot" style={{ background: c.couleur }} />
                            {c.label}
                        </label>
                    ))}
                </div>
            </PanneauFlottant>

            {/* ══ PANNEAU 2 — TYPES × COLONNES ═════════════════════════════ */}
            <PanneauFlottant
                ouvert={panneaux.types}
                titre="Types de journées à afficher"
                icone="fa-palette"
                largeur={760}
                hauteur="52vh"
                onFermer={() => ouvrir('types', false)}
                sousBarre={<span className="pl-hint">Par colonne — clic sur l'en-tête = toute la colonne · clic-glissé pour cocher en masse</span>}
                pied={<button className="pl-btn pl-btn-primary" onClick={() => ouvrir('types', false)}><i className="fa-solid fa-check" /> Terminé</button>}
            >
                <table className="pl-matrix">
                    <thead>
                        <tr>
                            <th className="pl-matrix-lbl">Type de journée</th>
                            {colonnesRetenues.map(c => (
                                <th
                                    key={c.cle}
                                    className="pl-matrix-col"
                                    onClick={() => basculerColonneEntiere(setTypesEteints, c.cle, typesJournees.map(t => t.code))}
                                    title="Basculer toute la colonne"
                                >
                                    <span className="pl-dot" style={{ background: c.couleur }} />
                                    {c.label.replace('BUT', 'B')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {typesJournees.map(t => (
                            <tr key={t.code}>
                                <td className="pl-matrix-lbl">
                                    <span className="pl-cfg-apercu" style={{ background: t.couleur, color: t.couleur_texte || '#fff' }}>{t.code}</span>
                                    {' '}{t.libelle}
                                </td>
                                {colonnesRetenues.map(c => {
                                    const cle = c.cle + '|' + t.code;
                                    const allume = !typesEteints.has(cle);
                                    return (
                                        <td
                                            key={cle}
                                            className={'pl-matrix-cell' + (allume ? '' : ' off')}
                                            style={allume ? { background: t.couleur, color: t.couleur_texte || '#fff' } : undefined}
                                            onMouseDown={e => debuterPeinture(setTypesEteints, cle, e)}
                                            onMouseEnter={() => survolPeinture(setTypesEteints, cle)}
                                        >
                                            {allume ? t.code : ''}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </PanneauFlottant>

            {/* ══ PANNEAU 3 — ÉVÉNEMENTS × COLONNES ════════════════════════ */}
            <PanneauFlottant
                ouvert={panneaux.evenements}
                titre="Événements importants"
                icone="fa-thumbtack"
                largeur={1020}
                hauteur="82vh"
                hautInitial={40}
                onFermer={() => ouvrir('evenements', false)}
                sousBarre={
                    <div className="pl-exp-sousbarre">
                        <input
                            className="pl-f pl-exp-recherche"
                            placeholder="Rechercher un événement…"
                            value={recherche}
                            onChange={e => setRecherche(e.target.value)}
                        />
                        {/* « Tout » et « Aucun » ne touchent que les colonnes RETENUES,
                            comme la maquette : décocher une colonne de l'export ne doit
                            pas effacer les choix qu'on y avait faits, on peut vouloir
                            la remettre. */}
                        <button className="pl-btn pl-btn-sm" onClick={() => toutEvenements(true)}>Tout</button>
                        <button className="pl-btn pl-btn-sm" onClick={() => toutEvenements(false)}>Aucun</button>
                        <span className="pl-hint">Le libellé se renomme pour le PDF — le nom de l'événement, lui, ne change pas</span>
                    </div>
                }
                pied={<button className="pl-btn pl-btn-primary" onClick={() => ouvrir('evenements', false)}><i className="fa-solid fa-check" /> Terminé</button>}
            >
                <table className="pl-matrix">
                    <thead>
                        <tr>
                            <th className="pl-matrix-lbl">Événement ({evenements.length})</th>
                            {colonnesRetenues.map(c => (
                                <th
                                    key={c.cle}
                                    className="pl-matrix-col"
                                    onClick={() => basculerColonneEntiere(setEvtEteints, c.cle, evenements.map(e => e.id))}
                                    title="Basculer toute la colonne"
                                >
                                    <span className="pl-dot" style={{ background: c.couleur }} />
                                    {c.label.replace('BUT', 'B')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {evtFiltres.map(e => (
                            <tr key={e.id}>
                                <td className="pl-matrix-lbl">
                                    <span className="pl-dot" style={{ background: e.couleur }} />
                                    <input
                                        className="pl-f pl-exp-rename"
                                        value={libelles[e.id] !== undefined ? libelles[e.id] : e.libelle}
                                        onChange={ev => setLibelles(l => ({ ...l, [e.id]: ev.target.value }))}
                                        title="Renommer pour le PDF"
                                    />
                                    <span className="pl-hint">{e.date.slice(8, 10)}/{e.date.slice(5, 7)}</span>
                                </td>
                                {colonnesRetenues.map(c => {
                                    const cle = c.cle + '|' + e.id;
                                    const allume = !evtEteints.has(cle);
                                    return (
                                        <td
                                            key={cle}
                                            className={'pl-matrix-cell' + (allume ? '' : ' off')}
                                            style={allume ? { background: e.couleur, color: '#fff' } : undefined}
                                            onMouseDown={ev => debuterPeinture(setEvtEteints, cle, ev)}
                                            onMouseEnter={() => survolPeinture(setEvtEteints, cle)}
                                        >
                                            {allume ? <i className="fa-solid fa-check" /> : ''}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {!evtFiltres.length && (
                            <tr>
                                <td className="pl-matrix-lbl" colSpan={colonnesRetenues.length + 1}>
                                    <span className="pl-hint">Aucun événement ne correspond à la recherche.</span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </PanneauFlottant>

            {/* ══ NOMMER UN MODÈLE ═════════════════════════════════════════
                Une saisie de texte, donc une modale — la charte du module interdit
                `window.prompt`, et c'est ce qui reste du dernier retiré au lot 5.2. */}
            {/* Lot PA.3 — « Envoyer » : le PDF de CE modèle, refait au moment de lancer, joint à chaque message */}
            {envoi && (
                <PlanningAnnuelEnvoi
                    departementId={config?.departement?.id}
                    modele={envoi}
                    fabriquerPdf={() => construirePdf(reglagesDepuisConfig(envoi.config)).pdf}
                    modifiable={peutEcrire}
                    onFermer={() => setEnvoi(null)}
                />
            )}

            {nouveauModele && (
                <ModalePlanning
                    titre="Enregistrer ce modèle"
                    icone="fa-floppy-disk"
                    onFermer={() => setNouveauModele(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setNouveauModele(null)}>Annuler</button>
                            <button
                                className="pl-btn pl-btn-primary"
                                onClick={() => enregistrerModele(nouveauModele.nom.trim())}
                                disabled={enregistrement || !nouveauModele.nom.trim()}
                            >
                                <i className="fa-solid fa-floppy-disk" /> Enregistrer
                            </button>
                        </>
                    }
                >
                    <p>
                        Le modèle garde les colonnes, les types de journées, les événements retenus
                        et le titre. Il est <b>partagé avec le département</b>.
                    </p>
                    <input
                        className="pl-f pl-exp-titre"
                        value={nouveauModele.nom}
                        autoFocus
                        maxLength={250}
                        placeholder="Nom du modèle — par exemple « BUT 1 FI »"
                        onChange={e => { const nom = e.target.value; setNouveauModele(n => ({ ...n, nom })); }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && nouveauModele.nom.trim() && !enregistrement) {
                                enregistrerModele(nouveauModele.nom.trim());
                            }
                        }}
                    />
                    {/* v94 (lot PA.2) : le modèle devient une vignette de l'accueil du module */}
                    <label className="pl-exp-surAccueil">
                        <input
                            type="checkbox"
                            checked={nouveauModele.surAccueil === true}
                            onChange={e => { const surAccueil = e.target.checked; setNouveauModele(n => ({ ...n, surAccueil })); }}
                        />
                        Mettre ce modèle <b>sur l'accueil</b> du Planning annuel (une vignette : Télécharger le PDF en un geste)
                    </label>
                </ModalePlanning>
            )}

            {confirmation && (
                <ModalePlanning
                    titre={confirmation.titre}
                    icone={confirmation.icone}
                    onFermer={() => setConfirmation(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setConfirmation(null)}>Annuler</button>
                            <button
                                className={'pl-btn ' + (confirmation.danger ? 'pl-btn-danger' : 'pl-btn-primary')}
                                onClick={confirmation.action}
                                disabled={enregistrement}
                            >
                                {confirmation.libelle}
                            </button>
                        </>
                    }
                >
                    <p>{confirmation.message}</p>
                </ModalePlanning>
            )}

            {information && (
                <ModalePlanning
                    titre={information.titre}
                    icone="fa-circle-info"
                    onFermer={() => setInformation(null)}
                    pied={<button className="pl-btn pl-btn-primary" onClick={() => setInformation(null)}>Fermer</button>}
                >
                    <p>{information.message}</p>
                </ModalePlanning>
            )}
        </div>
    );
}

export default PlanningExportCalendrier;

